// src/components/GestaoDiarioBordo.jsx
// =============================================================================
// GESTÃO DO DIÁRIO DE BORDO  ·  Gestão de Frota  ·  v2 (torre de comando)
// -----------------------------------------------------------------------------
// Consolida (por tenant) os dados que o app DiarioDeBordo.js sincroniza:
// inspeções pré-jornada, validade de documentos, score de prontidão e streaks.
//
// Núcleo do pedido:
//   • Visão consolidada por motorista
//   • Push de lembrete (reaproveita a infra de push do projeto)
//   • Obrigar a inspeção + detectar quem não inspeciona há muito tempo
//
// +recursos:
//   1. Torre de Conformidade (anel SVG animado + distribuição da frota)
//   2. Mural de Vencimentos (timeline proativa 7/15/30/60/90 dias)
//   3. Radar de Itens Críticos (sinal de manutenção preventiva)
//   4. Ranking de Confiabilidade + Streaks (gamificação / ofensores)
//   5. Exportação consolidada em Excel (SheetJS) + Timeline drill-down
//   6. Aba "Documentos" — matriz motorista × tipo de documento com datas
//   7. [NOVO] Revisão de CNH — a coluna CNH da matriz ganha um botão de revisão
//      que abre um modal com os dados extraídos por IA, as fotos (frente/verso,
//      carregadas via blob autenticado — não expostas por link direto do Drive),
//      e permite ao admin confirmar ou corrigir manualmente os campos.
//
// [CORREÇÃO] hojeISO() ajusta para o fuso local (mesma lógica do app mobile).
//
// Contrato de dados idêntico à v1, com o acréscimo de:
//   GET  /api/diario-bordo/documentos
//   GET  /api/diario-bordo/cnh/:motoristaId
//   POST /api/diario-bordo/cnh/:motoristaId/revisar
// =============================================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
  FaSyncAlt, FaSearch, FaBell, FaShieldAlt, FaFileExcel, FaTimes, FaCheck,
  FaExclamationTriangle, FaClipboardCheck, FaUserClock, FaFireAlt, FaTrophy,
  FaCalendarAlt, FaChartLine, FaUserCheck, FaTruck, FaPaperPlane, FaLock,
  FaLockOpen, FaArrowRight, FaIdCard, FaTools, FaSkullCrossbones, FaMedal,
  FaUsers, FaBolt, FaRegClock, FaEye, FaExternalLinkAlt, FaCamera,
} from 'react-icons/fa';
import './GestaoDiarioBordo.css';
import PainelEvidencias from './PainelEvidencias';

// ── Mapas de rótulo (espelho do app; back só guarda chaves) ─────────────────
const DOC_LABELS = {
  cnh: 'CNH', crlv: 'Licenciamento (CRLV)', seguro: 'Seguro do Veículo',
  aso: 'ASO (Exame Médico)', tacografo: 'Tacógrafo', extintor: 'Extintor',
};
const ITEM_LABELS = {
  pneus: 'Pneus e estepe', freios: 'Freios', luzes: 'Luzes e setas',
  oleo: 'Óleo do motor', agua: 'Água / arrefecimento', parabrisa: 'Para-brisa / limpadores',
  cabo: 'Cabo de aço / guincho', lanca: 'Lança / plataforma', correntes: 'Correntes e cintas',
  hidraulico: 'Sistema hidráulico', sinalizacao: 'Sinalização (cones/triângulo)',
  rotativa: 'Luz rotativa / strobo', docs_bordo: 'Documentos a bordo', extintor_b: 'Extintor ao alcance',
};
const labelDoc = (k) => DOC_LABELS[k] || k;
const labelItem = (k) => ITEM_LABELS[k] || k;

// [NOVO] Status da revisão de CNH → cor/label/ícone
const CNH_STATUS_CFG = {
  confirmado: { cor: '#16a34a', bg: '#dcfce7', label: 'Confirmada' },
  revisao_manual: { cor: '#f59e0b', bg: '#fef3c7', label: 'Aguardando revisão' },
  falha_leitura: { cor: '#ef4444', bg: '#fee2e2', label: 'Falha na leitura' },
};

// ── Helpers ─────────────────────────────────────────────────────────────────
// Ajusta para o fuso horário local antes de extrair a data ISO, igual ao
// helper usado no DiarioDeBordo.js (app mobile).
const hojeISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const fmtBR = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const iniciais = (n = '') => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');
const fmtDataExtenso = () =>
  new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

// Máscara progressiva de digitação de data (dd/mm/aaaa)
const mascaraDataBR = (raw) => {
  const dig = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (dig.length <= 2) return dig;
  if (dig.length <= 4) return `${dig.slice(0, 2)}/${dig.slice(2)}`;
  return `${dig.slice(0, 2)}/${dig.slice(2, 4)}/${dig.slice(4)}`;
};
const brParaISO = (br) => {
  const m = String(br || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  const dia = +d, mes = +mo;
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
  return `${y}-${mo}-${d}`;
};

// Confiabilidade composta (0–100) a partir do consolidado.
function confiabilidade(m) {
  let s = 100;
  if (m.ultima_inspecao == null) s -= 45;
  else s -= Math.min(40, (Number(m.dias_sem_inspecao) || 0) * 8);
  s -= Math.min(30, (Number(m.docs_vencidos) || 0) * 12);
  s -= Math.min(18, (Number(m.ultimos_criticos) || 0) * 6);
  s += Math.min(10, Number(m.streak_dias) || 0);
  return Math.max(0, Math.min(100, Math.round(s)));
}
const corScore = (v) => (v >= 85 ? '#16a34a' : v >= 60 ? '#f59e0b' : '#ef4444');
const rotuloScore = (v) => (v >= 85 ? 'Frota pronta' : v >= 60 ? 'Atenção parcial' : 'Requer ação');

// Classifica o motorista em um estado operacional
function statusMotorista(m) {
  if (m.ultima_inspecao == null) return { key: 'sem', label: 'Nunca inspecionou', cor: '#ef4444' };
  if (m.ultima_inspecao === hojeISO()) return { key: 'hoje', label: 'Inspeção de hoje OK', cor: '#16a34a' };
  const d = Number(m.dias_sem_inspecao) || 0;
  if (d >= 3) return { key: 'critico', label: `Sem inspeção há ${d}d`, cor: '#ef4444' };
  return { key: 'atrasado', label: `Sem inspeção há ${d}d`, cor: '#f59e0b' };
}

// Janela de vencimento → faixa
function faixaVenc(dias) {
  if (dias < 0) return { key: 'vencido', label: 'Vencidos', cor: '#ef4444' };
  if (dias <= 7) return { key: '7', label: 'Até 7 dias', cor: '#ef4444' };
  if (dias <= 15) return { key: '15', label: '8–15 dias', cor: '#f59e0b' };
  if (dias <= 30) return { key: '30', label: '16–30 dias', cor: '#f59e0b' };
  if (dias <= 60) return { key: '60', label: '31–60 dias', cor: '#0ea5e9' };
  return { key: '90', label: '61–90 dias', cor: '#64748b' };
}

// ── Anel de prontidão (SVG com progresso animado) ───────────────────────────
function RingGauge({ value = 0, size = 132, stroke = 11 }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (v / 100) * c;
  const cor = corScore(v);
  return (
    <div className="dbg-gauge" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.14)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={cor} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} className="dbg-gauge-arc"
        />
      </svg>
      <div className="dbg-gauge-center">
        <span className="dbg-gauge-val" style={{ color: cor }}>{v}</span>
        <span className="dbg-gauge-max">/100</span>
      </div>
    </div>
  );
}

// ── Sparkline minúsculo (SVG inline) ────────────────────────────────────────
function Sparkline({ series = [], width = 120, height = 32 }) {
  const pts = series.filter((p) => p.score != null);
  if (pts.length < 2) return <span className="dbg-spark-empty">sem histórico</span>;
  const max = 100, min = 0;
  const step = width / (pts.length - 1);
  const coords = pts.map((p, i) => [i * step, height - ((p.score - min) / (max - min)) * height]);
  const d = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${d} L${width},${height} L0,${height} Z`;
  const last = pts[pts.length - 1].score;
  const cor = corScore(last);
  return (
    <svg className="dbg-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id="dbgSparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={cor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={cor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#dbgSparkFill)" stroke="none" />
      <path d={d} fill="none" stroke={cor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="3" fill={cor} />
    </svg>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function Kpi({ icon, label, value, sub, tone = 'navy', onClick }) {
  return (
    <button className={`dbg-kpi dbg-kpi--${tone} ${onClick ? 'is-clickable' : ''}`} onClick={onClick} type="button">
      <div className="dbg-kpi-icon">{icon}</div>
      <div className="dbg-kpi-body">
        <span className="dbg-kpi-value">{value}</span>
        <span className="dbg-kpi-label">{label}</span>
        {sub ? <span className="dbg-kpi-sub">{sub}</span> : null}
      </div>
      {onClick ? <FaArrowRight className="dbg-kpi-go" /> : null}
    </button>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export default function GestaoDiarioBordo() {
  const [aba, setAba] = useState('painel'); // painel | motoristas | documentos | vencimentos | radar | ranking
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [motoristas, setMotoristas] = useState([]);
  const [vencimentos, setVencimentos] = useState([]);
  const [radar, setRadar] = useState({ janela_dias: 30, amostras: 0, itens: [] });

  // Matriz completa de documentos por motorista (sem filtro de janela)
  const [documentosPorMotorista, setDocumentosPorMotorista] = useState([]);

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos'); // todos|hoje|atrasado|critico|sem
  const [selecao, setSelecao] = useState(new Set());

  // drawer timeline
  const [drawerId, setDrawerId] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // modais
  const [pushModal, setPushModal] = useState(null);    // { ids:[], titulo, msg }
  const [exigirModal, setExigirModal] = useState(null); // { ids:[], bloquear, msg }
  const [enviando, setEnviando] = useState(false);

  // [NOVO] Modal de revisão da CNH
  // { motoristaId, motoristaNome, loading, ficha, arquivos: {frente,verso,pdf} (blob URLs),
  //   form: {nome, numero_registro, categoria, validadeBR, orgao_emissor}, salvando }
  const [cnhModal, setCnhModal] = useState(null);

  // [NOVO] Upload manual da CNH pelo CRM: { frente, verso, pdf } (File|null) + enviando
  const [cnhUpload, setCnhUpload] = useState({ frente: null, verso: null, pdf: null, enviando: false });

  // ── carga ───────────────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m, v, r, doc] = await Promise.all([
        api.get('/api/diario-bordo/dashboard'),
        api.get('/api/diario-bordo/motoristas'),
        api.get('/api/diario-bordo/vencimentos?dias=90'),
        api.get('/api/diario-bordo/itens-criticos?dias=30'),
        api.get('/api/diario-bordo/documentos'),
      ]);
      setDashboard(d.data);
      setMotoristas(m.data || []);
      setVencimentos(v.data || []);
      setRadar(r.data || { itens: [] });
      setDocumentosPorMotorista(doc.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Falha ao carregar o Diário de Bordo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Limpeza dos object URLs criados para as fotos da CNH ao fechar o modal
  useEffect(() => {
    return () => {
      if (cnhModal?.arquivos) {
        Object.values(cnhModal.arquivos).forEach((url) => {
          if (url) URL.revokeObjectURL(url);
        });
      }
    };
  }, [cnhModal]);

  // ── derivados ─────────────────────────────────────────────────────────────
  const motoristasView = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return motoristas
      .map((m) => ({ ...m, _status: statusMotorista(m), _conf: confiabilidade(m) }))
      .filter((m) => (!t || m.motorista_nome.toLowerCase().includes(t) || (m.email || '').toLowerCase().includes(t)))
      .filter((m) => (filtroStatus === 'todos' ? true : m._status.key === filtroStatus));
  }, [motoristas, busca, filtroStatus]);

  const ofensores = useMemo(
    () => [...motoristas]
      .map((m) => ({ ...m, _conf: confiabilidade(m) }))
      .filter((m) => m.ultima_inspecao == null || (Number(m.dias_sem_inspecao) || 0) >= 2)
      .sort((a, b) => {
        const da = a.ultima_inspecao == null ? 9999 : (Number(a.dias_sem_inspecao) || 0);
        const db = b.ultima_inspecao == null ? 9999 : (Number(b.dias_sem_inspecao) || 0);
        return db - da;
      }),
    [motoristas]
  );

  const ranking = useMemo(
    () => [...motoristas]
      .map((m) => ({ ...m, _conf: confiabilidade(m) }))
      .sort((a, b) => (b._conf - a._conf) || ((b.streak_dias || 0) - (a.streak_dias || 0))),
    [motoristas]
  );

  const vencPorFaixa = useMemo(() => {
    const ordem = ['vencido', '7', '15', '30', '60', '90'];
    const grupos = {};
    vencimentos.forEach((v) => {
      const f = faixaVenc(Number(v.dias));
      grupos[f.key] = grupos[f.key] || { ...f, itens: [] };
      grupos[f.key].itens.push(v);
    });
    return ordem.filter((k) => grupos[k]).map((k) => grupos[k]);
  }, [vencimentos]);

  // Distribuição da frota (usa dashboard; cai p/ motoristas se faltar)
  const distribuicao = useMemo(() => {
    const total = motoristas.length;
    const emDia = dashboard?.em_dia ?? motoristas.filter((m) => statusMotorista(m).key === 'hoje').length;
    const atras = dashboard?.atrasados ?? motoristas.filter((m) => ['atrasado', 'critico'].includes(statusMotorista(m).key)).length;
    const sem = dashboard?.sem_inspecao ?? motoristas.filter((m) => m.ultima_inspecao == null).length;
    const base = Math.max(1, total || emDia + atras + sem);
    return {
      total,
      emDia, atras, sem,
      pEmDia: (emDia / base) * 100,
      pAtras: (atras / base) * 100,
      pSem: (sem / base) * 100,
    };
  }, [dashboard, motoristas]);

  // Maior streak da frota (destaque leve)
  const topStreak = useMemo(
    () => motoristas.reduce((mx, m) => Math.max(mx, Number(m.streak_dias) || 0), 0),
    [motoristas]
  );

  // Chaves de documento para montar as colunas da matriz
  const DOC_KEYS = useMemo(() => Object.keys(DOC_LABELS), []);

  // Matriz motorista × documento
  const matrizDocumentos = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const porMotorista = {};

    motoristas.forEach((m) => {
      porMotorista[m.motorista_id] = {
        motorista_id: m.motorista_id,
        motorista_nome: m.motorista_nome,
        docs: {},
      };
    });

    documentosPorMotorista.forEach((d) => {
      if (!porMotorista[d.motorista_id]) {
        porMotorista[d.motorista_id] = {
          motorista_id: d.motorista_id,
          motorista_nome: d.motorista_nome,
          docs: {},
        };
      }
      porMotorista[d.motorista_id].docs[d.doc_key] = { validade: d.validade, dias: d.dias };
    });

    return Object.values(porMotorista)
      .filter((m) => !t || m.motorista_nome.toLowerCase().includes(t))
      .sort((a, b) => a.motorista_nome.localeCompare(b.motorista_nome));
  }, [motoristas, documentosPorMotorista, busca]);

  // Cor por dias restantes até o vencimento (mesma escala do resto da tela)
  const corValidade = (dias) => {
    if (dias == null) return '#94a3b8';
    if (dias < 0) return '#ef4444';
    if (dias <= 30) return '#f59e0b';
    return '#16a34a';
  };

  // ── seleção ────────────────────────────────────────────────────────────────
  const toggleSel = (id) => setSelecao((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selecionarVisiveis = () => setSelecao(new Set(motoristasView.map((m) => m.motorista_id)));
  const limparSelecao = () => setSelecao(new Set());

  // ── ações ────────────────────────────────────────────────────────────────
  const abrirPush = (ids, preset = {}) => {
    if (!ids.length) return toast.info('Selecione ao menos um motorista.');
    setPushModal({ ids, titulo: preset.titulo || '📋 Diário de Bordo', msg: preset.msg || '' });
  };
  const enviarPush = async () => {
    if (!pushModal?.msg.trim()) return toast.info('Escreva a mensagem.');
    setEnviando(true);
    try {
      const { data } = await api.post('/api/diario-bordo/push', {
        motorista_ids: pushModal.ids, titulo: pushModal.titulo, mensagem: pushModal.msg,
      });
      toast.success(data.message || 'Lembrete enviado.');
      setPushModal(null); limparSelecao();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao enviar push.');
    } finally { setEnviando(false); }
  };

  const abrirExigir = (ids) => {
    if (!ids.length) return toast.info('Selecione ao menos um motorista.');
    setExigirModal({ ids, bloquear: false, msg: '' });
  };
  const confirmarExigir = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post('/api/diario-bordo/exigir-inspecao', {
        motorista_ids: exigirModal.ids,
        bloquear_operacao: exigirModal.bloquear,
        mensagem: exigirModal.msg,
      });
      toast.success(data.message || 'Inspeção exigida.');
      setExigirModal(null); limparSelecao(); carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao exigir inspeção.');
    } finally { setEnviando(false); }
  };
  const cancelarExigir = async (id) => {
    try {
      await api.delete(`/api/diario-bordo/exigir-inspecao/${id}`);
      toast.info('Obrigatoriedade cancelada.');
      carregar();
    } catch { toast.error('Falha ao cancelar.'); }
  };

  const abrirTimeline = async (id) => {
    setDrawerId(id); setTimeline(null); setTimelineLoading(true);
    try {
      const { data } = await api.get(`/api/diario-bordo/motoristas/${id}/timeline`);
      setTimeline(data);
    } catch { toast.error('Falha ao carregar a timeline.'); }
    finally { setTimelineLoading(false); }
  };

  // [NOVO] Carrega um arquivo do Drive via blob autenticado (o axios já injeta
  // o Bearer token nesta instância `api`). Um link direto <a href> não
  // funcionaria aqui porque o endpoint /api/anexos/drive/:fileId exige o
  // header Authorization, que só o axios consegue anexar.
  const carregarArquivoDrive = async (fileId) => {
    if (!fileId) return null;
    try {
      const resp = await api.get(`/api/anexos/drive/${fileId}`, { responseType: 'blob' });
      return URL.createObjectURL(resp.data);
    } catch {
      return null;
    }
  };

  // [NOVO] Abre o modal de revisão de CNH: busca a ficha + carrega as fotos
  const abrirCnhModal = async (motoristaId, motoristaNome) => {
    setCnhModal({
      motoristaId, motoristaNome, loading: true, ficha: null,
      arquivos: {}, form: null, salvando: false,
    });
    setCnhUpload({ frente: null, verso: null, pdf: null, enviando: false });
    try {
      const { data } = await api.get(`/api/diario-bordo/cnh/${motoristaId}`);
      if (!data.existe) {
        setCnhModal((prev) => (prev ? { ...prev, loading: false, ficha: false } : prev));
        return;
      }

      const [frenteUrl, versoUrl, pdfUrl] = await Promise.all([
        carregarArquivoDrive(data.drive_file_id_frente),
        carregarArquivoDrive(data.drive_file_id_verso),
        carregarArquivoDrive(data.drive_file_id_pdf),
      ]);

      setCnhModal((prev) => (prev ? {
        ...prev,
        loading: false,
        ficha: data,
        arquivos: { frente: frenteUrl, verso: versoUrl, pdf: pdfUrl },
        form: {
          nome: data.nome_extraido || '',
          numero_registro: data.numero_registro || '',
          categoria: data.categoria || '',
          validadeBR: data.validade ? fmtBR(data.validade) : '',
          orgao_emissor: data.orgao_emissor || '',
        },
      } : prev));
    } catch (e) {
      toast.error('Falha ao carregar os dados da CNH.');
      setCnhModal(null);
    }
  };

  const fecharCnhModal = () => {
    if (cnhModal?.arquivos) {
      Object.values(cnhModal.arquivos).forEach((url) => { if (url) URL.revokeObjectURL(url); });
    }
    setCnhModal(null);
    setCnhUpload({ frente: null, verso: null, pdf: null, enviando: false });
  };

  // [NOVO] Seleciona um arquivo (frente/verso/pdf) para o upload manual.
  // Escolher um PDF limpa frente/verso e vice-versa, pois são fluxos
  // alternativos (mesma regra do app mobile).
  const selecionarArquivoCnh = (campo, file) => {
    if (!file) return;
    setCnhUpload((prev) => {
      if (campo === 'pdf') return { ...prev, pdf: file, frente: null, verso: null };
      return { ...prev, [campo]: file, pdf: null };
    });
  };

  const limparUploadCnh = () => setCnhUpload({ frente: null, verso: null, pdf: null, enviando: false });

  // [NOVO] Envia frente/verso ou PDF em nome do motorista aberto no modal,
  // reaproveitando o MESMO endpoint que o app usa (POST /cnh/upload) — a
  // única diferença é o campo motorista_id, que o backend só aceita de quem
  // tem perfil admin/admin_geral (ver routesDiarioBordo.js).
  const enviarUploadCnh = async () => {
    if (!cnhModal) return;
    const { frente, verso, pdf } = cnhUpload;
    if (!frente && !pdf) {
      toast.info('Selecione ao menos a foto da frente ou o PDF da CNH.');
      return;
    }

    setCnhUpload((prev) => ({ ...prev, enviando: true }));
    try {
      const formData = new FormData();
      formData.append('motorista_id', String(cnhModal.motoristaId));
      if (frente) formData.append('frente', frente);
      if (verso) formData.append('verso', verso);
      if (pdf) formData.append('pdf', pdf);

      const { data } = await api.post('/api/diario-bordo/cnh/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const statusMsg = {
        confirmado: 'CNH enviada e confirmada automaticamente pela IA.',
        revisao_manual: 'CNH enviada — os dados extraídos ficaram com confiança baixa e precisam de revisão.',
        falha_leitura: 'CNH enviada, mas a IA não conseguiu ler o documento. Revise manualmente abaixo.',
      };
      toast.success(statusMsg[data.status_validacao] || 'CNH enviada.');

      limparUploadCnh();
      // Recarrega a ficha (agora com os novos dados/fotos) e a matriz por trás.
      await abrirCnhModal(cnhModal.motoristaId, cnhModal.motoristaNome);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao enviar a CNH.');
      setCnhUpload((prev) => ({ ...prev, enviando: false }));
    }
  };

  const salvarRevisaoCnh = async () => {
    if (!cnhModal?.form) return;
    const { nome, numero_registro, categoria, validadeBR, orgao_emissor } = cnhModal.form;

    let validadeISO = null;
    if (validadeBR) {
      validadeISO = brParaISO(validadeBR);
      if (!validadeISO) {
        toast.error('Validade inválida. Use o formato DD/MM/AAAA.');
        return;
      }
    }

    setCnhModal((prev) => ({ ...prev, salvando: true }));
    try {
      await api.post(`/api/diario-bordo/cnh/${cnhModal.motoristaId}/revisar`, {
        nome: nome || null,
        numero_registro: numero_registro || null,
        categoria: categoria || null,
        validade: validadeISO,
        orgao_emissor: orgao_emissor || null,
      });
      toast.success('Dados da CNH confirmados.');
      fecharCnhModal();
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao confirmar os dados da CNH.');
      setCnhModal((prev) => (prev ? { ...prev, salvando: false } : prev));
    }
  };

  const exportarExcel = () => {
    const linhas = ranking.map((m) => ({
      Motorista: m.motorista_nome,
      'E-mail': m.email || '',
      'Última inspeção': fmtBR(m.ultima_inspecao),
      'Dias sem inspeção': m.ultima_inspecao == null ? 'nunca' : (m.dias_sem_inspecao ?? 0),
      'Score (último)': m.ultimo_score ?? '—',
      'Streak (dias)': m.streak_dias ?? 0,
      'Confiabilidade': m._conf,
      'Docs vencidos': m.docs_vencidos ?? 0,
      'Docs vencendo (30d)': m.docs_vencendo ?? 0,
      'Próx. vencimento': fmtBR(m.proximo_venc),
      'Críticos (última)': m.ultimos_criticos ?? 0,
      'Inspeção exigida': m.inspecao_exigida ? 'SIM' : 'não',
      'Push disponível': m.tem_push ? 'sim' : 'NÃO',
      'Último sync': m.ultimo_sync || '—',
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(linhas);
    ws['!cols'] = Object.keys(linhas[0] || { a: 1 }).map(() => ({ wch: 18 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Diário de Bordo');
    XLSX.writeFile(wb, `diario-bordo-${hojeISO()}.xlsx`);
    toast.success('Planilha gerada.');
  };

  const drawerMotorista = motoristas.find((m) => m.motorista_id === drawerId);

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="dbg-page">
      {/* HEADER */}
      <header className="dbg-header">
        <div className="dbg-header-title">
          <div className="dbg-header-ic"><FaClipboardCheck /></div>
          <div>
            <h1>Diário de Bordo</h1>
            <p>Prontidão, inspeções e documentos da frota — consolidado por motorista.</p>
          </div>
        </div>
        <div className="dbg-header-actions">
          <span className="dbg-header-date"><FaRegClock /> {fmtDataExtenso()}</span>
          <button className="dbg-btn dbg-btn-ghost" onClick={exportarExcel} disabled={loading || !motoristas.length}>
            <FaFileExcel /> Exportar
          </button>
          <button className="dbg-btn dbg-btn-ghost dbg-btn-icon" onClick={carregar} disabled={loading} title="Recarregar">
            <FaSyncAlt className={loading ? 'spin' : ''} />
          </button>
        </div>
      </header>

      {/* TORRE DE CONFORMIDADE */}
      {dashboard && (
        <section className="dbg-tower">
          <div className="dbg-tower-hero" style={{ '--ring': corScore(dashboard.indice_prontidao) }}>
            <RingGauge value={dashboard.indice_prontidao} />
            <div className="dbg-tower-info">
              <span className="dbg-tower-eyebrow">Índice de Prontidão da Frota</span>
              <strong className="dbg-tower-status" style={{ color: '#fff' }}>
                {rotuloScore(dashboard.indice_prontidao)}
              </strong>
              <p>Inspeção do dia + conformidade documental, ponderados por motorista ativo.</p>

              <div className="dbg-tower-chips">
                <span className="dbg-tower-chip"><FaUsers /> {distribuicao.total} motoristas</span>
                <span className="dbg-tower-chip"><FaBolt /> score médio {dashboard.score_medio}</span>
                {topStreak > 0 && <span className="dbg-tower-chip"><FaFireAlt /> maior streak {topStreak}d</span>}
              </div>

              <div className="dbg-distrib" role="img"
                aria-label={`Em dia ${distribuicao.emDia}, atrasados ${distribuicao.atras}, nunca ${distribuicao.sem}`}>
                <div className="dbg-distrib-bar">
                  <span style={{ width: `${distribuicao.pEmDia}%`, background: '#22c55e' }} />
                  <span style={{ width: `${distribuicao.pAtras}%`, background: '#f59e0b' }} />
                  <span style={{ width: `${distribuicao.pSem}%`, background: '#ef4444' }} />
                </div>
                <div className="dbg-distrib-legend">
                  <span><i style={{ background: '#22c55e' }} /> {distribuicao.emDia} em dia</span>
                  <span><i style={{ background: '#f59e0b' }} /> {distribuicao.atras} atrasados</span>
                  <span><i style={{ background: '#ef4444' }} /> {distribuicao.sem} nunca</span>
                </div>
              </div>
            </div>
          </div>

          <div className="dbg-kpi-grid">
            <Kpi tone="green" icon={<FaUserCheck />} value={dashboard.em_dia} label="Em dia (hoje)" onClick={() => { setAba('motoristas'); setFiltroStatus('hoje'); }} />
            <Kpi tone="amber" icon={<FaUserClock />} value={dashboard.atrasados} label="Atrasados" onClick={() => { setAba('motoristas'); setFiltroStatus('atrasado'); }} />
            <Kpi tone="red" icon={<FaSkullCrossbones />} value={dashboard.sem_inspecao} label="Nunca inspecionaram" onClick={() => { setAba('motoristas'); setFiltroStatus('sem'); }} />
            <Kpi tone="red" icon={<FaIdCard />} value={dashboard.docs_vencidos} label="Documentos vencidos" sub={`${dashboard.docs_vencendo} vencendo em 30d`} onClick={() => setAba('vencimentos')} />
            <Kpi tone="navy" icon={<FaExclamationTriangle />} value={dashboard.criticos_abertos} label="Itens críticos hoje" onClick={() => setAba('radar')} />
            <Kpi tone="purple" icon={<FaShieldAlt />} value={dashboard.inspecao_exigida} label="Inspeção exigida" />
          </div>
        </section>
      )}

      {/* TABS */}
      <nav className="dbg-tabs">
        {[
          { k: 'painel', t: 'Painel', i: <FaChartLine /> },
          { k: 'motoristas', t: 'Motoristas', i: <FaTruck /> },
          { k: 'documentos', t: 'Documentos', i: <FaIdCard /> },
          { k: 'evidencias', t: 'Evidências', i: <FaCamera /> },
          { k: 'vencimentos', t: 'Vencimentos', i: <FaCalendarAlt /> },
          { k: 'radar', t: 'Radar de itens', i: <FaTools /> },
          { k: 'ranking', t: 'Ranking', i: <FaTrophy /> },
        ].map((tab) => (
          <button key={tab.k} className={`dbg-tab ${aba === tab.k ? 'is-active' : ''}`} onClick={() => setAba(tab.k)}>
            {tab.i} {tab.t}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="dbg-loading"><FaSyncAlt className="spin" /> Carregando…</div>
      ) : (
        <main className="dbg-main">

          {/* ───────────── PAINEL (bento) ───────────── */}
          {aba === 'painel' && (
            <div className="dbg-bento">
              <div className="dbg-card dbg-cell dbg-cell--ofensores">
                <div className="dbg-card-head">
                  <h3><FaUserClock /> Ofensores — quem não inspeciona</h3>
                  {ofensores.length > 0 && (
                    <button className="dbg-btn dbg-btn-sm dbg-btn-warn"
                      onClick={() => abrirExigir(ofensores.map((m) => m.motorista_id))}>
                      <FaShieldAlt /> Exigir de todos
                    </button>
                  )}
                </div>
                {ofensores.length === 0 ? (
                  <div className="dbg-empty dbg-empty--tall"><FaCheck /> Frota em dia. Ninguém atrasado. 🎉</div>
                ) : (
                  <ul className="dbg-offender-list">
                    {ofensores.slice(0, 10).map((m) => (
                      <li key={m.motorista_id}>
                        <div className="dbg-avatar dbg-avatar-sm">{iniciais(m.motorista_nome)}</div>
                        <div className="dbg-offender-info">
                          <strong>{m.motorista_nome}</strong>
                          <span>{m.ultima_inspecao == null ? 'Nunca inspecionou' : `Há ${m.dias_sem_inspecao} dia(s)`}</span>
                        </div>
                        {m.inspecao_exigida
                          ? <span className="dbg-badge dbg-badge-purple"><FaShieldAlt /> exigida</span>
                          : <button className="dbg-btn dbg-btn-sm dbg-btn-warn" onClick={() => abrirExigir([m.motorista_id])}>Exigir</button>}
                        <button className="dbg-icon-btn" title="Lembrar por push" onClick={() => abrirPush([m.motorista_id], { msg: `Olá ${m.motorista_nome.split(' ')[0]}! Faça a inspeção pré-jornada no app antes de rodar.` })}>
                          <FaBell />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="dbg-card dbg-cell dbg-cell--radar">
                <div className="dbg-card-head">
                  <h3><FaTools /> Radar — itens mais problemáticos (30d)</h3>
                  <button className="dbg-btn dbg-btn-sm dbg-btn-ghost" onClick={() => setAba('radar')}>ver tudo</button>
                </div>
                {radar.itens.length === 0 ? (
                  <div className="dbg-empty">Sem inspeções recentes para analisar.</div>
                ) : (
                  <ul className="dbg-radar-list">
                    {radar.itens.slice(0, 6).map((it) => {
                      const pct = it.total ? Math.round(((it.criticos + it.atencao) / it.total) * 100) : 0;
                      return (
                        <li key={it.item}>
                          <div className="dbg-radar-top">
                            <span>{labelItem(it.item)}</span>
                            <b style={{ color: it.criticos ? '#ef4444' : '#f59e0b' }}>{it.criticos} crít · {it.atencao} aten</b>
                          </div>
                          <div className="dbg-bar"><div className="dbg-bar-fill" style={{ width: `${pct}%`, background: it.criticos ? '#ef4444' : '#f59e0b' }} /></div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="dbg-card dbg-cell dbg-cell--venc">
                <div className="dbg-card-head"><h3><FaCalendarAlt /> Próximos vencimentos</h3>
                  <button className="dbg-btn dbg-btn-sm dbg-btn-ghost" onClick={() => setAba('vencimentos')}>ver tudo</button>
                </div>
                {vencimentos.length === 0 ? <div className="dbg-empty">Nenhum documento vencendo em 90 dias.</div> : (
                  <ul className="dbg-venc-mini">
                    {vencimentos.slice(0, 6).map((v, i) => {
                      const f = faixaVenc(Number(v.dias));
                      return (
                        <li key={i}>
                          <span className="dbg-dot" style={{ background: f.cor }} />
                          <div className="dbg-venc-mini-info">
                            <strong>{labelDoc(v.doc_key)}</strong>
                            <span>{v.motorista_nome}</span>
                          </div>
                          <span className="dbg-venc-dias" style={{ color: f.cor }}>
                            {Number(v.dias) < 0 ? `vencido` : `${v.dias}d`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="dbg-card dbg-cell dbg-cell--ranking">
                <div className="dbg-card-head"><h3><FaMedal /> Top confiabilidade</h3>
                  <button className="dbg-btn dbg-btn-sm dbg-btn-ghost" onClick={() => setAba('ranking')}>ranking</button>
                </div>
                <ul className="dbg-podium">
                  {ranking.slice(0, 5).map((m, i) => (
                    <li key={m.motorista_id}>
                      <span className={`dbg-rank-pos pos-${i + 1}`}>{i + 1}</span>
                      <div className="dbg-avatar dbg-avatar-sm">{iniciais(m.motorista_nome)}</div>
                      <strong>{m.motorista_nome}</strong>
                      {(m.streak_dias || 0) > 0 && <span className="dbg-streak"><FaFireAlt /> {m.streak_dias}</span>}
                      <span className="dbg-conf-pill" style={{ color: corScore(m._conf), borderColor: corScore(m._conf) }}>{m._conf}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* ───────────── MOTORISTAS ───────────── */}
          {aba === 'motoristas' && (
            <div className="dbg-card">
              <div className="dbg-toolbar">
                <div className="dbg-search">
                  <FaSearch />
                  <input placeholder="Buscar motorista…" value={busca} onChange={(e) => setBusca(e.target.value)} />
                </div>
                <div className="dbg-filters">
                  {[['todos', 'Todos'], ['hoje', 'Em dia'], ['atrasado', 'Atrasados'], ['critico', '3+ dias'], ['sem', 'Nunca']].map(([k, t]) => (
                    <button key={k} className={filtroStatus === k ? 'is-active' : ''} onClick={() => setFiltroStatus(k)}>{t}</button>
                  ))}
                </div>
              </div>

              {selecao.size > 0 && (
                <div className="dbg-bulkbar">
                  <span><b>{selecao.size}</b> selecionado(s)</span>
                  <div className="dbg-bulk-actions">
                    <button className="dbg-btn dbg-btn-sm dbg-btn-navy" onClick={() => abrirPush([...selecao])}><FaPaperPlane /> Push em massa</button>
                    <button className="dbg-btn dbg-btn-sm dbg-btn-warn" onClick={() => abrirExigir([...selecao])}><FaShieldAlt /> Exigir inspeção</button>
                    <button className="dbg-btn dbg-btn-sm dbg-btn-ghost" onClick={limparSelecao}>Limpar</button>
                  </div>
                </div>
              )}

              <div className="dbg-table-wrap">
                <table className="dbg-table">
                  <thead>
                    <tr>
                      <th className="dbg-th-check">
                        <input type="checkbox"
                          checked={motoristasView.length > 0 && selecao.size >= motoristasView.length}
                          onChange={(e) => (e.target.checked ? selecionarVisiveis() : limparSelecao())} />
                      </th>
                      <th>Motorista</th>
                      <th>Status</th>
                      <th>Última inspeção</th>
                      <th>Score</th>
                      <th>Streak</th>
                      <th>Documentos</th>
                      <th>Confiab.</th>
                      <th className="dbg-th-acoes">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {motoristasView.map((m) => (
                      <tr key={m.motorista_id} className={m.inspecao_exigida ? 'row-exigida' : ''}>
                        <td><input type="checkbox" checked={selecao.has(m.motorista_id)} onChange={() => toggleSel(m.motorista_id)} /></td>
                        <td>
                          <button className="dbg-name-cell" onClick={() => abrirTimeline(m.motorista_id)}>
                            <div className="dbg-avatar dbg-avatar-sm">{iniciais(m.motorista_nome)}</div>
                            <div className="dbg-name-info">
                              <strong>{m.motorista_nome}</strong>
                              {!m.tem_push && <span className="dbg-nopush" title="Sem token de push"><FaBell /> sem push</span>}
                            </div>
                          </button>
                        </td>
                        <td><span className="dbg-status-pill" style={{ color: m._status.cor, background: `${m._status.cor}1a` }}>{m._status.label}</span></td>
                        <td>{fmtBR(m.ultima_inspecao)}</td>
                        <td>{m.ultimo_score != null ? <b style={{ color: corScore(m.ultimo_score) }}>{m.ultimo_score}</b> : '—'}</td>
                        <td>{(m.streak_dias || 0) > 0 ? <span className="dbg-streak"><FaFireAlt /> {m.streak_dias}</span> : '—'}</td>
                        <td>
                          {m.docs_vencidos > 0 && <span className="dbg-badge dbg-badge-red">{m.docs_vencidos} venc.</span>}
                          {m.docs_vencendo > 0 && <span className="dbg-badge dbg-badge-amber">{m.docs_vencendo} em 30d</span>}
                          {!m.docs_vencidos && !m.docs_vencendo && <span className="dbg-muted">ok</span>}
                        </td>
                        <td><span className="dbg-conf-pill" style={{ color: corScore(m._conf), borderColor: corScore(m._conf) }}>{m._conf}</span></td>
                        <td>
                          <div className="dbg-row-actions">
                            <button className="dbg-icon-btn" title="Push" onClick={() => abrirPush([m.motorista_id])}><FaBell /></button>
                            {m.inspecao_exigida
                              ? <button className="dbg-icon-btn is-on" title="Cancelar obrigatoriedade" onClick={() => cancelarExigir(m.motorista_id)}><FaLockOpen /></button>
                              : <button className="dbg-icon-btn" title="Exigir inspeção" onClick={() => abrirExigir([m.motorista_id])}><FaLock /></button>}
                            <button className="dbg-icon-btn" title="Ver timeline" onClick={() => abrirTimeline(m.motorista_id)}><FaArrowRight /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {motoristasView.length === 0 && (
                      <tr><td colSpan={9}><div className="dbg-empty">Nenhum motorista encontrado.</div></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ───────────── DOCUMENTOS POR MOTORISTA ───────────── */}
          {aba === 'documentos' && (
            <div className="dbg-card">
              <div className="dbg-toolbar">
                <div className="dbg-search">
                  <FaSearch />
                  <input placeholder="Buscar motorista…" value={busca} onChange={(e) => setBusca(e.target.value)} />
                </div>
              </div>
              <div className="dbg-table-wrap">
                <table className="dbg-table">
                  <thead>
                    <tr>
                      <th>Motorista</th>
                      {DOC_KEYS.map((k) => <th key={k}>{labelDoc(k)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {matrizDocumentos.map((m) => (
                      <tr key={m.motorista_id}>
                        <td>
                          <div className="dbg-name-cell" style={{ cursor: 'default' }}>
                            <div className="dbg-avatar dbg-avatar-sm">{iniciais(m.motorista_nome)}</div>
                            <div className="dbg-name-info"><strong>{m.motorista_nome}</strong></div>
                          </div>
                        </td>
                        {DOC_KEYS.map((k) => {
                          const doc = m.docs[k];
                          const cor = corValidade(doc?.dias);

                          // Coluna da CNH ganha botão de revisão + sino de
                          // lembrete, além da data (quando já confirmada).
                          if (k === 'cnh') {
                            return (
                              <td key={k}>
                                <div className="dbg-cnh-cell">
                                  <div>
                                    <span style={{ color: cor, fontWeight: 700 }}>
                                      {doc?.validade ? fmtBR(doc.validade) : '—'}
                                    </span>
                                    {doc?.dias != null && (
                                      <div style={{ fontSize: 11, color: cor }}>
                                        {doc.dias < 0 ? `venc. há ${Math.abs(doc.dias)}d` : `${doc.dias}d`}
                                      </div>
                                    )}
                                  </div>
                                  <div className="dbg-cnh-cell-acoes">
                                    <button
                                      className="dbg-icon-btn"
                                      title="Ver / revisar CNH"
                                      onClick={() => abrirCnhModal(m.motorista_id, m.motorista_nome)}
                                    >
                                      <FaEye />
                                    </button>
                                    <button
                                      className="dbg-icon-btn"
                                      title="Lembrar de enviar a CNH"
                                      onClick={() => abrirPush([m.motorista_id], {
                                        titulo: '🪪 Envio da CNH',
                                        msg: 'Envie uma foto da sua CNH (frente e verso) ou o PDF no Diário de Bordo para regularizar seu cadastro.',
                                      })}
                                    >
                                      <FaBell />
                                    </button>
                                  </div>
                                </div>
                              </td>
                            );
                          }

                          return (
                            <td key={k}>
                              <span style={{ color: cor, fontWeight: 700 }}>
                                {doc?.validade ? fmtBR(doc.validade) : '—'}
                              </span>
                              {doc?.dias != null && (
                                <div style={{ fontSize: 11, color: cor }}>
                                  {doc.dias < 0 ? `venc. há ${Math.abs(doc.dias)}d` : `${doc.dias}d`}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {matrizDocumentos.length === 0 && (
                      <tr><td colSpan={DOC_KEYS.length + 1}><div className="dbg-empty">Nenhum motorista encontrado.</div></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ───────────── EVIDÊNCIAS ───────────── */}
          {aba === 'evidencias' && <PainelEvidencias />}

          {/* ───────────── VENCIMENTOS ───────────── */}
          {aba === 'vencimentos' && (
            <div className="dbg-venc-wall">
              {vencPorFaixa.length === 0 && <div className="dbg-card"><div className="dbg-empty"><FaCheck /> Nenhum documento vence nos próximos 90 dias.</div></div>}
              {vencPorFaixa.map((g) => (
                <div key={g.key} className="dbg-card dbg-venc-col">
                  <div className="dbg-venc-col-head" style={{ '--cor': g.cor }}>
                    <span>{g.label}</span><b>{g.itens.length}</b>
                  </div>
                  <ul>
                    {g.itens.map((v, i) => (
                      <li key={i} className="dbg-venc-item">
                        <div className="dbg-venc-item-info">
                          <strong>{labelDoc(v.doc_key)}</strong>
                          <span>{v.motorista_nome} · {fmtBR(v.validade)}</span>
                        </div>
                        <span className="dbg-venc-dias" style={{ color: g.cor }}>{Number(v.dias) < 0 ? `há ${Math.abs(v.dias)}d` : `${v.dias}d`}</span>
                        <button className="dbg-icon-btn" title="Lembrar motorista"
                          onClick={() => abrirPush([v.motorista_id], { titulo: '📄 Documento vencendo', msg: `${labelDoc(v.doc_key)} ${Number(v.dias) < 0 ? 'está vencido' : `vence em ${v.dias} dia(s)`} (${fmtBR(v.validade)}). Regularize a tempo.` })}>
                          <FaBell />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* ───────────── RADAR ───────────── */}
          {aba === 'radar' && (
            <div className="dbg-card">
              <div className="dbg-card-head">
                <h3><FaTools /> Itens críticos da frota — últimos {radar.janela_dias} dias</h3>
                <span className="dbg-muted">{radar.amostras} inspeção(ões) analisada(s)</span>
              </div>
              {radar.itens.length === 0 ? <div className="dbg-empty">Sem dados para o período.</div> : (
                <div className="dbg-radar-full">
                  {radar.itens.map((it) => {
                    const pctCrit = it.total ? (it.criticos / it.total) * 100 : 0;
                    const pctAt = it.total ? (it.atencao / it.total) * 100 : 0;
                    return (
                      <div key={it.item} className="dbg-radar-row">
                        <div className="dbg-radar-label">{labelItem(it.item)}</div>
                        <div className="dbg-radar-track">
                          <div className="dbg-radar-seg" style={{ width: `${pctCrit}%`, background: '#ef4444' }} title={`${it.criticos} crítico(s)`} />
                          <div className="dbg-radar-seg" style={{ width: `${pctAt}%`, background: '#f59e0b' }} title={`${it.atencao} atenção`} />
                        </div>
                        <div className="dbg-radar-nums">
                          <b style={{ color: '#ef4444' }}>{it.criticos}</b> / <b style={{ color: '#f59e0b' }}>{it.atencao}</b>
                          <span className="dbg-muted"> de {it.total}</span>
                        </div>
                      </div>
                    );
                  })}
                  <p className="dbg-radar-hint"><FaExclamationTriangle /> Itens recorrentes em vermelho são forte candidato a manutenção preventiva da frota.</p>
                </div>
              )}
            </div>
          )}

          {/* ───────────── RANKING ───────────── */}
          {aba === 'ranking' && (
            <div className="dbg-card">
              <div className="dbg-card-head"><h3><FaTrophy /> Ranking de confiabilidade & ofensividade</h3></div>
              <div className="dbg-rank-list">
                {ranking.map((m, i) => (
                  <div key={m.motorista_id} className="dbg-rank-row">
                    <span className={`dbg-rank-pos pos-${i + 1 <= 3 ? i + 1 : 'n'}`}>{i + 1}</span>
                    <div className="dbg-avatar dbg-avatar-sm">{iniciais(m.motorista_nome)}</div>
                    <div className="dbg-rank-info">
                      <strong>{m.motorista_nome}</strong>
                      <span>Última: {fmtBR(m.ultima_inspecao)} · {m.docs_vencidos || 0} doc(s) vencido(s)</span>
                    </div>
                    {(m.streak_dias || 0) > 0 && <span className="dbg-streak"><FaFireAlt /> {m.streak_dias}d</span>}
                    <div className="dbg-conf-bar">
                      <div className="dbg-conf-bar-fill" style={{ width: `${m._conf}%`, background: corScore(m._conf) }} />
                    </div>
                    <span className="dbg-conf-pill" style={{ color: corScore(m._conf), borderColor: corScore(m._conf) }}>{m._conf}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      )}

      {/* ───────────── DRAWER TIMELINE ───────────── */}
      {drawerId && (
        <div className="dbg-drawer-backdrop" onClick={() => setDrawerId(null)}>
          <aside className="dbg-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dbg-drawer-head">
              <div className="dbg-drawer-id">
                <div className="dbg-avatar">{iniciais(drawerMotorista?.motorista_nome || '')}</div>
                <div>
                  <h3>{drawerMotorista?.motorista_nome || 'Motorista'}</h3>
                  <p>{drawerMotorista?.email}</p>
                </div>
              </div>
              <button className="dbg-icon-btn" onClick={() => setDrawerId(null)}><FaTimes /></button>
            </div>

            {timelineLoading || !timeline ? (
              <div className="dbg-loading"><FaSyncAlt className="spin" /> Carregando…</div>
            ) : (
              <div className="dbg-drawer-body">
                <div className="dbg-drawer-actions">
                  <button className="dbg-btn dbg-btn-sm dbg-btn-navy" onClick={() => abrirPush([drawerId])}><FaBell /> Push</button>
                  {drawerMotorista?.inspecao_exigida
                    ? <button className="dbg-btn dbg-btn-sm dbg-btn-ghost" onClick={() => cancelarExigir(drawerId)}><FaLockOpen /> Liberar</button>
                    : <button className="dbg-btn dbg-btn-sm dbg-btn-warn" onClick={() => abrirExigir([drawerId])}><FaShieldAlt /> Exigir</button>}
                </div>

                <div className="dbg-drawer-section">
                  <h4><FaChartLine /> Evolução do score</h4>
                  <Sparkline series={timeline.score_series} width={380} height={64} />
                </div>

                <div className="dbg-drawer-section">
                  <h4><FaIdCard /> Documentos</h4>
                  {timeline.documentos.length === 0 ? <p className="dbg-muted">Nenhum documento cadastrado.</p> : (
                    <ul className="dbg-doc-list">
                      {timeline.documentos.map((d) => {
                        const dias = d.dias;
                        const cor = dias == null ? '#94a3b8' : dias < 0 ? '#ef4444' : dias <= 30 ? '#f59e0b' : '#16a34a';
                        return (
                          <li key={d.doc_key}>
                            <span>{labelDoc(d.doc_key)}</span>
                            <span style={{ color: cor }}>
                              {d.validade ? fmtBR(d.validade) : 'sem data'}
                              {dias != null && ` (${dias < 0 ? `venc. há ${Math.abs(dias)}d` : `${dias}d`})`}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="dbg-drawer-section">
                  <h4><FaClipboardCheck /> Histórico de inspeções</h4>
                  {timeline.inspecoes.length === 0 ? <p className="dbg-muted">Nenhuma inspeção registrada.</p> : (
                    <ul className="dbg-insp-list">
                      {timeline.inspecoes.map((i) => (
                        <li key={i.id}>
                          <div className="dbg-insp-date">{fmtBR(i.data_inspecao)}</div>
                          <div className="dbg-insp-badges">
                            <span className="dbg-badge dbg-badge-green">{i.qtd_ok} ok</span>
                            {i.qtd_atencao > 0 && <span className="dbg-badge dbg-badge-amber">{i.qtd_atencao} aten</span>}
                            {i.qtd_criticos > 0 && <span className="dbg-badge dbg-badge-red">{i.qtd_criticos} crít</span>}
                          </div>
                          {i.score != null && <span className="dbg-insp-score" style={{ color: corScore(i.score) }}>{i.score}</span>}
                          {i.observacao && <p className="dbg-insp-obs">“{i.observacao}”</p>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* ───────────── MODAL PUSH ───────────── */}
      {pushModal && (
        <div className="dbg-modal-backdrop" onClick={() => !enviando && setPushModal(null)}>
          <div className="dbg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dbg-modal-head"><h3><FaPaperPlane /> Enviar lembrete</h3>
              <button className="dbg-icon-btn" onClick={() => setPushModal(null)}><FaTimes /></button></div>
            <div className="dbg-modal-body">
              <p className="dbg-modal-target">Para <b>{pushModal.ids.length}</b> motorista(s).</p>
              <label>Título</label>
              <input value={pushModal.titulo} onChange={(e) => setPushModal({ ...pushModal, titulo: e.target.value })} maxLength={60} />
              <label>Mensagem</label>
              <textarea rows={3} value={pushModal.msg} onChange={(e) => setPushModal({ ...pushModal, msg: e.target.value })} maxLength={240}
                placeholder="Ex.: Não esqueça de fazer a inspeção pré-jornada hoje." />
              <div className="dbg-quick">
                {['Faça a inspeção pré-jornada hoje no app.', 'Atualize a validade dos seus documentos no Diário de Bordo.', 'Você está com documento vencendo — regularize.'].map((q, i) => (
                  <button key={i} onClick={() => setPushModal({ ...pushModal, msg: q })}>{q}</button>
                ))}
              </div>
            </div>
            <div className="dbg-modal-foot">
              <button className="dbg-btn dbg-btn-ghost" onClick={() => setPushModal(null)} disabled={enviando}>Cancelar</button>
              <button className="dbg-btn dbg-btn-navy" onClick={enviarPush} disabled={enviando}>
                {enviando ? <><FaSyncAlt className="spin" /> Enviando…</> : <><FaPaperPlane /> Enviar</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────── MODAL EXIGIR ───────────── */}
      {exigirModal && (
        <div className="dbg-modal-backdrop" onClick={() => !enviando && setExigirModal(null)}>
          <div className="dbg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dbg-modal-head"><h3><FaShieldAlt /> Exigir inspeção</h3>
              <button className="dbg-icon-btn" onClick={() => setExigirModal(null)}><FaTimes /></button></div>
            <div className="dbg-modal-body">
              <p className="dbg-modal-target">Vai obrigar <b>{exigirModal.ids.length}</b> motorista(s) a fazer a inspeção. Eles recebem push imediato e o app sinaliza a pendência.</p>
              <label className="dbg-check">
                <input type="checkbox" checked={exigirModal.bloquear} onChange={(e) => setExigirModal({ ...exigirModal, bloquear: e.target.checked })} />
                <span><FaLock /> Bloquear operação até inspecionar <em>(o app impede iniciar a jornada)</em></span>
              </label>
              <label>Mensagem (opcional)</label>
              <textarea rows={2} value={exigirModal.msg} onChange={(e) => setExigirModal({ ...exigirModal, msg: e.target.value })} maxLength={200}
                placeholder="A central exige a inspeção pré-jornada antes de você rodar hoje." />
            </div>
            <div className="dbg-modal-foot">
              <button className="dbg-btn dbg-btn-ghost" onClick={() => setExigirModal(null)} disabled={enviando}>Cancelar</button>
              <button className="dbg-btn dbg-btn-warn" onClick={confirmarExigir} disabled={enviando}>
                {enviando ? <><FaSyncAlt className="spin" /> Aplicando…</> : <><FaShieldAlt /> Exigir agora</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────── [NOVO] MODAL REVISÃO DE CNH ───────────── */}
      {cnhModal && (
        <div className="dbg-modal-backdrop" onClick={() => !cnhModal.salvando && fecharCnhModal()}>
          <div className="dbg-modal dbg-modal-cnh" onClick={(e) => e.stopPropagation()}>
            <div className="dbg-modal-head">
              <h3><FaIdCard /> CNH — {cnhModal.motoristaNome}</h3>
              <button className="dbg-icon-btn" onClick={fecharCnhModal}><FaTimes /></button>
            </div>

            <div className="dbg-modal-body">
              {/* [NOVO] Upload manual da CNH pelo CRM — disponível mesmo antes
                  de existir qualquer registro, e também para reenviar/substituir
                  os arquivos de um motorista que já tem CNH cadastrada. Reaproveita
                  o mesmo endpoint com IA que o app mobile usa. */}
              {!cnhModal.loading && (
                <div className="dbg-cnh-upload">
                  <div className="dbg-cnh-upload-head">
                    <span>Enviar CNH manualmente</span>
                    <span className="dbg-muted">Foto (frente + verso) ou PDF da CNH digital</span>
                  </div>

                  <div className="dbg-cnh-upload-campos">
                    <label className="dbg-cnh-upload-campo">
                      <span>Frente</span>
                      <input
                        type="file" accept="image/*"
                        onChange={(e) => selecionarArquivoCnh('frente', e.target.files?.[0])}
                        disabled={cnhUpload.enviando}
                      />
                      {cnhUpload.frente && <small>{cnhUpload.frente.name}</small>}
                    </label>
                    <label className="dbg-cnh-upload-campo">
                      <span>Verso</span>
                      <input
                        type="file" accept="image/*"
                        onChange={(e) => selecionarArquivoCnh('verso', e.target.files?.[0])}
                        disabled={cnhUpload.enviando || !!cnhUpload.pdf}
                      />
                      {cnhUpload.verso && <small>{cnhUpload.verso.name}</small>}
                    </label>
                    <label className="dbg-cnh-upload-campo">
                      <span>ou PDF</span>
                      <input
                        type="file" accept="application/pdf"
                        onChange={(e) => selecionarArquivoCnh('pdf', e.target.files?.[0])}
                        disabled={cnhUpload.enviando}
                      />
                      {cnhUpload.pdf && <small>{cnhUpload.pdf.name}</small>}
                    </label>
                  </div>

                  <div className="dbg-cnh-upload-acoes">
                    {(cnhUpload.frente || cnhUpload.verso || cnhUpload.pdf) && !cnhUpload.enviando && (
                      <button className="dbg-btn dbg-btn-ghost" onClick={limparUploadCnh}>Limpar</button>
                    )}
                    <button
                      className="dbg-btn dbg-btn-navy"
                      onClick={enviarUploadCnh}
                      disabled={cnhUpload.enviando || (!cnhUpload.frente && !cnhUpload.pdf)}
                    >
                      {cnhUpload.enviando
                        ? <><FaSyncAlt className="spin" /> Enviando e lendo…</>
                        : <><FaIdCard /> Enviar e processar</>}
                    </button>
                  </div>
                </div>
              )}

              {cnhModal.loading ? (
                <div className="dbg-loading"><FaSyncAlt className="spin" /> Carregando…</div>
              ) : cnhModal.ficha === false ? (
                <div className="dbg-empty">Este motorista ainda não enviou a CNH pelo app.</div>
              ) : (
                <>
                  {/* Status */}
                  {(() => {
                    const cfg = CNH_STATUS_CFG[cnhModal.ficha.status_validacao] || CNH_STATUS_CFG.revisao_manual;
                    return (
                      <div className="dbg-cnh-status" style={{ background: cfg.bg, color: cfg.cor }}>
                        <span>{cfg.label}</span>
                        {cnhModal.ficha.confianca_ia != null && (
                          <span className="dbg-cnh-confianca">confiança: {Math.round(cnhModal.ficha.confianca_ia * 100)}%</span>
                        )}
                      </div>
                    );
                  })()}
                  {cnhModal.ficha.motivo_revisao && (
                    <p className="dbg-cnh-motivo">Motivo: {cnhModal.ficha.motivo_revisao}</p>
                  )}

                  {/* Fotos / PDF */}
                  <div className="dbg-cnh-fotos">
                    {cnhModal.arquivos.frente && (
                      <a href={cnhModal.arquivos.frente} target="_blank" rel="noreferrer" className="dbg-cnh-foto">
                        <img src={cnhModal.arquivos.frente} alt="Frente da CNH" />
                        <span>Frente <FaExternalLinkAlt /></span>
                      </a>
                    )}
                    {cnhModal.arquivos.verso && (
                      <a href={cnhModal.arquivos.verso} target="_blank" rel="noreferrer" className="dbg-cnh-foto">
                        <img src={cnhModal.arquivos.verso} alt="Verso da CNH" />
                        <span>Verso <FaExternalLinkAlt /></span>
                      </a>
                    )}
                    {cnhModal.arquivos.pdf && (
                      <a href={cnhModal.arquivos.pdf} target="_blank" rel="noreferrer" className="dbg-cnh-foto dbg-cnh-foto-pdf">
                        <FaIdCard size={28} />
                        <span>Ver PDF <FaExternalLinkAlt /></span>
                      </a>
                    )}
                  </div>

                  {/* Formulário editável */}
                  <label>Nome</label>
                  <input
                    value={cnhModal.form.nome}
                    onChange={(e) => setCnhModal((prev) => ({ ...prev, form: { ...prev.form, nome: e.target.value } }))}
                  />
                  <label>Número de registro</label>
                  <input
                    value={cnhModal.form.numero_registro}
                    onChange={(e) => setCnhModal((prev) => ({ ...prev, form: { ...prev.form, numero_registro: e.target.value } }))}
                  />
                  <label>Categoria</label>
                  <input
                    value={cnhModal.form.categoria}
                    onChange={(e) => setCnhModal((prev) => ({ ...prev, form: { ...prev.form, categoria: e.target.value } }))}
                  />
                  <label>Validade</label>
                  <input
                    placeholder="DD/MM/AAAA"
                    value={cnhModal.form.validadeBR}
                    onChange={(e) => setCnhModal((prev) => ({ ...prev, form: { ...prev.form, validadeBR: mascaraDataBR(e.target.value) } }))}
                    maxLength={10}
                  />
                  <label>Órgão emissor</label>
                  <input
                    value={cnhModal.form.orgao_emissor}
                    onChange={(e) => setCnhModal((prev) => ({ ...prev, form: { ...prev.form, orgao_emissor: e.target.value } }))}
                  />
                </>
              )}
            </div>

            {cnhModal.ficha && cnhModal.ficha !== false && (
              <div className="dbg-modal-foot">
                <button className="dbg-btn dbg-btn-ghost" onClick={fecharCnhModal} disabled={cnhModal.salvando}>Cancelar</button>
                <button className="dbg-btn dbg-btn-navy" onClick={salvarRevisaoCnh} disabled={cnhModal.salvando}>
                  {cnhModal.salvando ? <><FaSyncAlt className="spin" /> Salvando…</> : <><FaCheck /> Confirmar dados</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}